/**
 * Admin Portal Router
 * 
 * Enterprise-grade admin interface with proper routing.
 * Single entry point for all admin functionality.
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminLayout } from './layout';

// Page imports - critical pages loaded eagerly
import { AdminDashboard } from './dashboard';
import { SystemDashboard } from './system';

import { lazyRetry } from '@/lib/lazyRetry';

// Lazy load less critical pages — with retry on chunk load failure

const DataVisIndex = lazyRetry(() => import('./data-vis'));
const ContentIndex = lazyRetry(() => import('./content'));
const CustomersIndex = lazyRetry(() => import('./customers'));
const SchedulingIndex = lazyRetry(() => import('./scheduling'));
const AnalyticsIndex = lazyRetry(() => import('./analytics'));
const CRMIndex = lazyRetry(() => import('./crm'));
const DocsRouter = lazyRetry(() => import('./docs/router'));

// Loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
  </div>
);

export const AdminRouter: React.FC = () => {
  return (
    <Routes>
      {/* All pages use AdminLayout with sidebar */}
      <Route path="*" element={
        <AdminLayout>
          <React.Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Dashboard - default */}
              <Route index element={<AdminDashboard />} />
              
              {/* System - consolidated health, monitoring & audit */}
              <Route path="system" element={<SystemDashboard />} />
              
              {/* Legacy redirects for operations/security */}
              <Route path="operations" element={<Navigate to="/admin/system" replace />} />
              <Route path="security" element={<Navigate to="/admin/system" replace />} />
              
              
              {/* Data Vis - superadmin only */}
              <Route path="data-vis/*" element={<DataVisIndex />} />

              {/* Content management */}
              <Route path="content/*" element={<ContentIndex />} />
              
              {/* Customer management (consolidated Users + Billing) */}
              <Route path="customers/*" element={<CustomersIndex />} />
              
              {/* Legacy redirects */}
              <Route path="users/*" element={<Navigate to="/admin/customers" replace />} />
              <Route path="billing/*" element={<Navigate to="/admin/customers/billing" replace />} />
              
              {/* Scheduling management */}
              <Route path="scheduling/*" element={<SchedulingIndex />} />
              
              {/* CRM - lead pipeline, messaging inbox, templates */}
              <Route path="crm/*" element={<CRMIndex />} />
              
              {/* Analytics - with sub-routes for progress/lexicon */}
              <Route path="analytics/*" element={<AnalyticsIndex />} />
              
              {/* Documentation */}
              <Route path="docs/*" element={<DocsRouter />} />
              
              {/* Legacy redirects - QR, Tools, Portal */}
              <Route path="qr" element={<Navigate to="/admin/content/qr" replace />} />
              <Route path="tools/*" element={<Navigate to="/admin/content/tools" replace />} />
              <Route path="portal/*" element={<Navigate to="/admin/content/settings" replace />} />
              
              {/* Settings - redirect to content settings */}
              <Route path="settings" element={<Navigate to="/admin/content/settings" replace />} />
              
              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </React.Suspense>
        </AdminLayout>
      } />
    </Routes>
  );
};

export default AdminRouter;
